import { createClient, SupabaseClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const REQUIRED_SUPABASE_ENV_VARS = ["VITE_SUPABASE_URL", "VITE_SUPABASE_ANON_KEY"] as const;

if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
        "[MediFlow] Supabase nicht konfiguriert. Bitte VITE_SUPABASE_URL und VITE_SUPABASE_ANON_KEY in .env.local setzen.",
    );
}

// Safe storage detection for SSR/test environments
const safeStorage =
    typeof window !== "undefined" && window.localStorage ? window.localStorage : undefined;

/**
 * Custom lock implementation using a simple in-memory mutex.
 * Replaces Navigator LockManager which causes 10s timeout deadlocks
 * when multiple Supabase auth calls compete for the same lock.
 * See: https://github.com/supabase/auth-js/issues/888
 */
const inMemoryLock: {
    acquiredLocks: Set<string>;
    queue: Array<{ name: string; resolve: () => void }>;
} = { acquiredLocks: new Set(), queue: [] };

function navigatorLockNoOp<R>(
    name: string,
    _acquireTimeout: number,
    fn: (lock: unknown) => Promise<R>,
): Promise<R> {
    // If lock is not held, acquire immediately
    if (!inMemoryLock.acquiredLocks.has(name)) {
        inMemoryLock.acquiredLocks.add(name);
        return fn(name).finally(() => {
            inMemoryLock.acquiredLocks.delete(name);
            // Process queue
            const next = inMemoryLock.queue.findIndex((q) => q.name === name);
            if (next >= 0) {
                const item = inMemoryLock.queue.splice(next, 1)[0];
                item?.resolve();
            }
        });
    }

    // Queue for lock
    return new Promise<void>((resolve) => {
        inMemoryLock.queue.push({ name, resolve });
    }).then(() => {
        inMemoryLock.acquiredLocks.add(name);
        return fn(name).finally(() => {
            inMemoryLock.acquiredLocks.delete(name);
            const next = inMemoryLock.queue.findIndex((q) => q.name === name);
            if (next >= 0) {
                const item = inMemoryLock.queue.splice(next, 1)[0];
                item?.resolve();
            }
        });
    });
}

export const supabase: SupabaseClient = createClient(
    supabaseUrl || "https://placeholder.supabase.co",
    supabaseAnonKey || "placeholder-key",
    {
        auth: {
            storage: safeStorage as any,
            autoRefreshToken: true,
            persistSession: Boolean(safeStorage),
            detectSessionInUrl: typeof window !== "undefined",
            lock: navigatorLockNoOp as any,
        },
        realtime: {
            params: { eventsPerSecond: 10 },
        },
    },
);

export function isSupabaseConfigured(): boolean {
    return Boolean(
        supabaseUrl && supabaseAnonKey && supabaseUrl !== "https://placeholder.supabase.co",
    );
}

export function getMissingSupabaseEnvVars(): Array<(typeof REQUIRED_SUPABASE_ENV_VARS)[number]> {
    return REQUIRED_SUPABASE_ENV_VARS.filter((envVar) => {
        if (envVar === "VITE_SUPABASE_URL") return !supabaseUrl;
        return !supabaseAnonKey;
    });
}

export function requireSupabase(): SupabaseClient {
    if (!isSupabaseConfigured()) {
        const missingEnvVars = getMissingSupabaseEnvVars();
        const suffix =
            missingEnvVars.length > 0
                ? ` Fehlend: ${missingEnvVars.join(", ")}.`
                : "";
        throw new Error(
            `Supabase ist nicht konfiguriert. Bitte Build-/Deploy-Umgebung prüfen.${suffix}`,
        );
    }
    return supabase;
}

export async function syncRealtimeAuth(accessToken?: string | null): Promise<void> {
    if (!isSupabaseConfigured() || !accessToken) return;

    try {
        await supabase.realtime.setAuth(accessToken);
    } catch (error) {
        console.warn("[MediFlow Praxis] Realtime-Auth konnte nicht gesetzt werden:", error);
    }
}

/**
 * Ensures a valid auth session exists before making API calls.
 * Refreshes the token if expired. Returns the current access token or null.
 */
export async function ensureValidSession(): Promise<string | null> {
    if (!isSupabaseConfigured()) return null;

    const {
        data: { session },
        error,
    } = await supabase.auth.getSession();

    if (error) {
        console.warn("[MediFlow] Session-Check fehlgeschlagen:", error.message);
        return null;
    }

    if (!session) {
        console.warn("[MediFlow] Keine aktive Session gefunden");
        return null;
    }

    const {
        data: { user: currentUser },
        error: userError,
    } = await supabase.auth.getUser(session.access_token);

    if (userError || !currentUser) {
        console.warn("[MediFlow] Session ungültig, versuche Refresh...", userError?.message);
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshData.session) {
            console.error("[MediFlow] Session-Refresh nach ungültiger Session fehlgeschlagen:", refreshError?.message);
            await supabase.auth.signOut().catch(() => undefined);
            return null;
        }

        await syncRealtimeAuth(refreshData.session.access_token);
        return refreshData.session.access_token;
    }

    // Check if token is about to expire (within 30 seconds)
    const expiresAt = session.expires_at ?? 0;
    const now = Math.floor(Date.now() / 1000);

    if (expiresAt - now < 30) {
        console.info("[MediFlow] Token läuft ab, versuche Refresh...");
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError || !refreshData.session) {
            console.error("[MediFlow] Session-Refresh fehlgeschlagen:", refreshError?.message);
            return null;
        }

        console.info("[MediFlow] Token erfolgreich erneuert");
        await syncRealtimeAuth(refreshData.session.access_token);
        return refreshData.session.access_token;
    }

    await syncRealtimeAuth(session.access_token);
    return session.access_token;
}
